<template>
  <el-dialog v-model="visible" :title="title" size="small" :close-on-click-modal="false" :close-on-press-escape="false" :show-close="false">
    <div class="outbreak-alert">
      <template v-if="isLinkMode">
        <p>Your facility appears to be in outbreak. This case matches a similar definition.</p>
        <p class="outbreak-alert__detail">
          Open outbreak: <strong>{{ alert.openOutbreak.typeValue }}</strong> ({{ alert.openOutbreak.scope }}), declared {{ formatDeclaredDate(alert.openOutbreak.declaredDate) }}.
        </p>
        <p><strong>Link this case to the outbreak record?</strong></p>
      </template>
      <template v-else>
        <p>{{ matchSummary }}</p>
        <el-table :data="alert.matchingCases" stripe class="outbreak-alert__cases">
          <el-table-column label="Onset" width="140">
            <template slot-scope="scope">{{ formatDate(scope.row.onsetDate) }}</template>
          </el-table-column>
          <el-table-column label="Unit">
            <template slot-scope="scope">{{ unitLabel(scope.row.unitId) }}</template>
          </el-table-column>
          <el-table-column label="Individual" width="120">
            <template slot-scope="scope">{{ clientTypeLabel(scope.row.clientType) }}</template>
          </el-table-column>
        </el-table>
        <p><strong>Would you like to open an outbreak record?</strong></p>
      </template>
    </div>
    <div slot="footer">
      <el-button data-testid="outbreak-alert-no" @click="$emit('dismiss')" :disabled="busy">No</el-button>
      <el-button data-testid="outbreak-alert-yes" type="primary" @click="confirm" :loading="busy">Yes</el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from "moment";
import { OutbreakScope } from "../Shared/ruleset.constants";
import { CLIENT_TYPE_LABELS, NO_UNIT_ID, NO_UNIT_LABEL } from "./outbreak-alert.constants";

// Presentational: shows one of the API's outbreakAlerts and asks one yes/no question. The parent
// (CreateInfectionCase) performs the link / create calls and closes this by unmounting it.
export default {
  name: "outbreak-alert-prompt",
  props: {
    alert: { type: Object, required: true },
    busy: { type: Boolean, default: false },
  },
  emits: ["dismiss", "link-outbreak", "create-outbreak"],
  data() {
    return { visible: true };
  },
  computed: {
    isLinkMode() {
      return !!this.alert.openOutbreak;
    },
    title() {
      return this.isLinkMode ? "Open Outbreak Found" : "Possible Outbreak";
    },
    matchSummary() {
      const { threshold, matchingCases } = this.alert;
      const scope = threshold.scope === OutbreakScope.Unit ? "on the same unit" : "in this facility";
      const count = matchingCases.length;
      return `${count} ${count === 1 ? "case" : "cases"} of this infection site ${scope} with onset within ${threshold.windowHours} hours, including the one just created:`;
    },
  },
  methods: {
    confirm() {
      this.$emit(this.isLinkMode ? "link-outbreak" : "create-outbreak");
    },
    // Onset is shown in local time, as the Surveillance list does.
    formatDate(iso) {
      return iso ? moment(iso).format(this.$configStore.dateFormat()) : "";
    },
    // Declared date is a UTC calendar date; OutbreakSummary and OutbreakDetail read it the same way.
    formatDeclaredDate(iso) {
      return iso ? moment.utc(iso).format(this.$configStore.dateFormat()) : "";
    },
    unitLabel(unitId) {
      if (unitId === NO_UNIT_ID) return NO_UNIT_LABEL;
      const unit = (this.$configStore.data.units || []).find((u) => u.id === unitId);
      return unit ? unit.displayText : "";
    },
    clientTypeLabel(clientType) {
      return CLIENT_TYPE_LABELS[clientType] || "";
    },
  },
};
</script>

<style scoped>
.outbreak-alert p {
  margin: 0 0 0.75rem;
}

.outbreak-alert__detail {
  color: #555;
}

.outbreak-alert__cases {
  width: 100%;
  margin-bottom: 0.75rem;
}
</style>
